import { useMemo } from "react";
import { useAuth } from "@/contexts/AuthProvider";
import { useConfiguration } from "@/contexts/ConfigProvider";
/**
 * Configuration interface for inactivity timeout settings
 */
export interface InactivityConfig {
	/** Time before showing warning modal (in milliseconds) */
	warningTimeout: number;
	/** Total time before automatic logout (in milliseconds) */
	logoutTimeout: number;
	/** Duration of warning phase (in milliseconds) */
	warningDuration: number;
	/** Whether the inactivity system is enabled */
	enabled: boolean;
	/** Events to monitor for user activity */
	events: string[];
}

/**
 * Default configuration values
 */
const DEFAULT_CONFIG: InactivityConfig = {
	warningTimeout: 25 * 60 * 1000, // 25 minutes
	logoutTimeout: 30 * 60 * 1000, // 30 minutes
	warningDuration: 5 * 60 * 1000, // 5 minutes
	enabled: true,
	events: [
		"mousemove",
		"mousedown",
		"resize",
		"keydown",
		"touchstart",
		"wheel",
		"scroll",
		"click",
	],
};

/**
 * Hook to get inactivity timeout configuration
 *
 * This hook integrates with your existing configuration system to provide
 * server-driven inactivity timeout settings with sensible fallbacks.
 *
 * Features:
 * - Server-driven configuration via ConfigProvider
 * - Fallback to default values when config unavailable
 * - Automatic calculation of warning duration
 * - Integration with authentication state
 * - Type-safe configuration values
 */
export function useInactivityConfig(): InactivityConfig {
	const { isAuthenticated } = useAuth();
	const config = useConfiguration();

	const inactivityConfig = useMemo(() => {
		// If user is not authenticated, disable the system
		if (!isAuthenticated) {
			return {
				...DEFAULT_CONFIG,
				enabled: false,
			};
		}

		// If config is not loaded yet, return defaults
		if (!config) {
			// console.log(
			// 	"⚙️ Inactivity Config: Using default values (config not loaded)",
			// );
			return DEFAULT_CONFIG;
		}

		// Extract inactivity settings from config with fallbacks
		const warningTimeout =
			config.security?.inactivityWarning ?? DEFAULT_CONFIG.warningTimeout;
		const logoutTimeout =
			config.security?.inactivityLogout ?? DEFAULT_CONFIG.logoutTimeout;

		// Calculate warning duration (time between warning and logout)
		const warningDuration = logoutTimeout - warningTimeout;

		// Validate configuration values
		if (warningTimeout >= logoutTimeout) {
			console.warn(
				"⚠️ Inactivity Config: Warning timeout must be less than logout timeout. Using defaults.",
			);
			return DEFAULT_CONFIG;
		}

		if (warningDuration < 30000) {
			// Less than 30 seconds warning
			console.warn(
				"⚠️ Inactivity Config: Warning duration is very short. Consider increasing the difference between warning and logout timeouts.",
			);
		}

		const resolvedConfig: InactivityConfig = {
			warningTimeout,
			logoutTimeout,
			warningDuration,
			enabled: config.security?.inactivityEnabled ?? DEFAULT_CONFIG.enabled,
			events: DEFAULT_CONFIG.events, // Keep events static for now
		};

		// console.log("⚙️ Inactivity Config: Resolved configuration", {
		// 	warningMinutes: warningTimeout / 1000 / 60,
		// 	logoutMinutes: logoutTimeout / 1000 / 60,
		// 	warningDurationMinutes: warningDuration / 1000 / 60,
		// 	enabled: resolvedConfig.enabled,
		// });

		return resolvedConfig;
	}, [config, isAuthenticated]);

	return inactivityConfig;
}

/**
 * Utility function to format timeout values for display
 */
export function formatTimeout(milliseconds: number): string {
	const minutes = Math.floor(milliseconds / 60000);
	const seconds = Math.floor((milliseconds % 60000) / 1000);

	if (minutes === 0) {
		return `${seconds}s`;
	}
	if (seconds === 0) {
		return `${minutes}m`;
	}
	return `${minutes}m ${seconds}s`;
}

/**
 * Utility function to validate inactivity configuration
 */
export function validateInactivityConfig(config: Partial<InactivityConfig>): {
	isValid: boolean;
	errors: string[];
} {
	const errors: string[] = [];

	if (config.warningTimeout && config.logoutTimeout) {
		if (config.warningTimeout >= config.logoutTimeout) {
			errors.push("Warning timeout must be less than logout timeout");
		}

		const warningDuration = config.logoutTimeout - config.warningTimeout;
		if (warningDuration < 10000) {
			// Less than 10 seconds
			errors.push("Warning duration should be at least 10 seconds");
		}
	}

	if (config.warningTimeout && config.warningTimeout < 60000) {
		// Less than 1 minute
		errors.push("Warning timeout should be at least 1 minute");
	}

	if (config.logoutTimeout && config.logoutTimeout < 120000) {
		// Less than 2 minutes
		errors.push("Logout timeout should be at least 2 minutes");
	}

	return {
		isValid: errors.length === 0,
		errors,
	};
}
